import { CalendarClock, ChartColumn, FileCheck, ReceiptText, Target, Wallet } from "lucide-react"
import { useEffect } from "react"
import { Navigate, useNavigate, useParams } from "react-router"

import { Pestanas } from "@/componentes/ui/Pestanas"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { GastosYCategorias } from "@/paginas/interno/administracion/GastosYCategorias"
import { Presupuesto } from "@/paginas/interno/administracion/Presupuesto"
import { Reportes } from "@/paginas/interno/administracion/Reportes"
import { GastosProgramados } from "@/paginas/interno/GastosProgramados"
import { Nomina } from "@/paginas/interno/nomina/Nomina"
import { OrdenesPago } from "@/paginas/interno/OrdenesPago"

/**
 * Las vistas del departamento, cada una con el permiso que la abre.
 *
 * ⚠ **El orden es el del circuito del gasto**, no el alfabético: se redacta la orden,
 * se paga a la gente, se consulta en qué se gasta y se programa lo que vuelve cada mes.
 */
const VISTAS = [
  {
    valor: "ordenes-pago",
    etiqueta: "Órdenes de pago",
    icono: FileCheck,
    permiso: "orden_pago:ver",
  },
  { valor: "nomina", etiqueta: "Nómina", icono: Wallet, permiso: "nomina:ver" },
  { valor: "gastos", etiqueta: "Gastos y categorías", icono: ReceiptText, permiso: "gasto:ver" },
  {
    valor: "programados",
    etiqueta: "Programados",
    icono: CalendarClock,
    permiso: "gasto_programado:ver",
  },
  // El tope con el que se contaba. Pide `gasto:ver` como el catálogo: quien decide qué
  // gastos existen es quien fija su tope.
  { valor: "presupuesto", etiqueta: "Presupuesto", icono: Target, permiso: "gasto:ver" },
  // Los reportes gerenciales en UNA entrada (28/10, minuta del 27/10, puntos 1 y 2): el
  // reporte general de cuentas y resultados y el informe de gastos, que tenía la suya.
  { valor: "reportes", etiqueta: "Reportes", icono: ChartColumn, permiso: "informe_gasto:ver" },
] as const

type Vista = (typeof VISTAS)[number]["valor"]

const SUBTITULO: Record<Vista, string> = {
  "ordenes-pago":
    "Lo que la agencia se compromete a pagar, con quién lo pidió y quién lo autorizó.",
  nomina: "Lo que cobra la gente de la casa: los conceptos que suman y restan, y cada corrida.",
  gastos: "En qué gasta la agencia y cómo se agrupa. Desde aquí se redactan las órdenes de pago.",
  programados:
    "Lo que vuelve cada mes. El sistema redacta la orden en borrador cuando toca y avisa; nada se aprueba ni se paga solo.",
  presupuesto:
    "Con cuánto se contaba en el período y cuánto va. Avisa cuando una partida se pasa; no frena ningún pago.",
  reportes:
    "Cuentas y resultados del período por categoría, y en qué se gastó. Semanal, 15, 30, 60 días o el mes.",
}

/**
 * Administración: el departamento, con sus vistas en pestañas.
 *
 * ⚠ **Un departamento, una entrada del menú.** Órdenes de pago y «Sueldos y salarios»
 * eran entradas sueltas y los gastos, sus categorías y los programados colgaban del
 * Catálogo, que es otra cosa —el catálogo dice QUÉ se compra y a QUIÉN; esto es el
 * trabajo del área que lo paga—. La barra lateral no crece con una línea por cada
 * pantalla de un mismo departamento.
 *
 * ⚠ **De la nómina entra sólo lo FINANCIERO: conceptos y períodos.** Los contratos son
 * la relación laboral y viven en «Talento humano». Es la decisión de la agencia: la
 * relación con el empleado se parte en dos, lo laboral y lo que cuesta, y esto segundo
 * lo lleva administración.
 *
 * ⚠ **Dos llaves, a propósito.** `administracion:ver` ofrece la entrada y cada pestaña
 * sigue pidiendo la suya —ver lo que gana la gente no es ver en qué se gasta—, porque
 * una fila de `modulos` lleva un solo permiso. Sólo salen las pestañas que se pueden
 * abrir; con la entrada y ninguna, la pantalla lo dice.
 *
 * ⚠ **La vista va en la URL** (`/administracion/gastos`) y se cambia con `replace`,
 * igual que en Tesorería: se puede recargar y compartir, y recorrer las pestañas no
 * apila historial. Las direcciones viejas —`/ordenes-pago`, `/nomina`,
 * `/catalogo/gastos`— llevan a su pestaña.
 *
 * ⚠ **Nómina trae su PROPIA fila de pestañas debajo** (`/administracion/nomina/periodos`).
 * Aplanar sus vistas aquí arriba habría dado una tira de siete y, sobre todo, habría
 * borrado «Nómina» como una cosa: lo que se mira junto es la corrida con sus conceptos.
 */
export function Administracion() {
  const usuario = usarSesion((e) => e.usuario)
  const { vista: enRuta } = useParams()
  const navegar = useNavigate()

  const visibles = VISTAS.filter((v) => tienePermiso(usuario, v.permiso))
  const vista: Vista | undefined =
    visibles.find((v) => v.valor === enRuta)?.valor ?? visibles[0]?.valor

  useEffect(() => {
    if (vista !== undefined && enRuta !== vista) {
      navegar(`/administracion/${vista}`, { replace: true })
    }
  }, [enRuta, vista, navegar])

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-xl font-semibold">Administración</h1>
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {vista ? SUBTITULO[vista] : "El gasto de la agencia, desde que se redacta."}
        </p>
      </header>

      {vista === undefined ? (
        <p className="tarjeta text-sm" style={{ color: "var(--fg-muted)" }}>
          No tienes acceso a ninguna de sus vistas. Cada una pide su propio permiso, y se conceden
          desde «Roles y permisos».
        </p>
      ) : (
        <>
          <Pestanas
            pestanas={visibles}
            activa={vista}
            onCambiar={(v) => navegar(`/administracion/${v}`, { replace: true })}
            etiqueta="Vistas de administración"
            idPanel="panel-administracion"
            prefijoId="pestana-administracion"
          />
          <div
            id="panel-administracion"
            role="tabpanel"
            aria-labelledby={`pestana-administracion-${vista}`}
            className="mt-4"
          >
            {vista === "ordenes-pago" && <OrdenesPago />}
            {vista === "nomina" && <Nomina />}
            {vista === "gastos" && <GastosYCategorias />}
            {vista === "programados" && <GastosProgramados />}
            {vista === "presupuesto" && <Presupuesto />}
            {vista === "reportes" && <Reportes />}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * `/nomina/<vista>`, la dirección de cuando «Sueldos y salarios» era una entrada suelta.
 *
 * ⚠ **Las tres vistas no fueron al mismo sitio, y por eso esto no es un `Navigate`
 * fijo:** los contratos se quedaron con lo laboral, en «Talento humano», y conceptos y
 * períodos pasaron a Administración. Un marcador de `/nomina/contratos` tiene que
 * seguir abriendo los contratos, no la corrida del mes.
 */
export function RutaNominaVieja() {
  const { vista } = useParams()
  if (vista === "contratos") return <Navigate to="/talento-humano/contratos" replace={true} />
  return <Navigate to={`/administracion/nomina/${vista ?? ""}`} replace={true} />
}
